import { useEffect, useMemo, useRef, useState } from 'react';
import { getCustomerSettingsColumns } from './columns';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../../types/store';
import { CommonDatagrid } from '..';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { CustomerSettingsService } from '../../../api/services/customerSettingsService';
import { IUpdateMetavalueRequest } from '../../../types/api/request/customersettingsrequest';
import { setCustomerSettings, updateMetavalue } from '../../../store/Reducers/CustomerSettingSlice';
import { RefreshButton } from '../../Buttons/RefreshButton';
import { AddCustomerSettingDialog } from '../../Dialog/AddCustomerSettingDialog';
import { Box } from '@mui/material';

export const CustomerSettingsDatagrid = () => {
  const dispatch = useDispatch();
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const editingRowIdRef = useRef<string | null>(null);
  editingRowIdRef.current = editingRowId;

  const [editingValue, setEditingValue] = useState<string>('');
  const editingValueRef = useRef<string>('');
  editingValueRef.current = editingValue;

  const { content: data } = useSelector((state: IRootState) => state.customerSettings);

  const retrieveAllCustomerSettings = async () => {
    try {
      const settings = await CustomerSettingsService.getAllCustomerSettings();
      dispatch(setCustomerSettings(settings));
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve customer settings. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const handleEdit = (id: string, value: string) => {
    setEditingRowId(id);
    setEditingValue(value);
  };

  const handleSave = async (id: string, newValue: string) => {
    try {
      const payload: IUpdateMetavalueRequest = {
        metakey: id,
        metavalue: newValue,
      };
      const res = await CustomerSettingsService.updateMetavalue(payload);
      dispatch(updateMetavalue(res));
      setEditingRowId(null);
      dispatch(
        showNotification({
          message: `Successfully updated metavalue.`,
        }),
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to update metavalue. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setEditingValue('');
  };

  const handleToggle = async (id: string, value: boolean) => {
    try {
      const payload: IUpdateMetavalueRequest = {
        metakey: id,
        metavalue: String(value),
      };
      const res = await CustomerSettingsService.updateMetavalue(payload);
      dispatch(updateMetavalue(res));
      dispatch(
        showNotification({
          message: `Successfully updated metavalue.`,
        }),
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to update metavalue. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const columns = useMemo(() => {
    return getCustomerSettingsColumns(
      editingRowIdRef,
      handleEdit,
      handleSave,
      handleCancel,
      setEditingValue,
      editingValueRef,
      handleToggle,
    );
  }, []);

  useEffect(() => {
    retrieveAllCustomerSettings();
  }, []);

  return (
    <>
      <CommonDatagrid
        data={data}
        columns={columns}
        rowId={'metakey'}
        isCellEditable={(params) => params.id === editingRowId && params.field === 'metavalue'}
        header={<p>Customer Settings</p>}
        headerActionButtons={
          <Box gap={2} display={'flex'}>
            <AddCustomerSettingDialog />
            <RefreshButton
              onClick={() => {
                retrieveAllCustomerSettings();
              }}
            />
          </Box>
        }
        searchbarPlaceholder={'Search setting by metakey'}
        searchKey={'metakey'}
      />
    </>
  );
};
